import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { UiControlValueAccessorDirective } from '../../basics/ui-control-value-accessor';
import { takeUntil } from 'rxjs';
import { getRandomId } from '../../utils/get-random-id';
import { BoschUiValidationErrorComponent } from '../bosch-ui-validation-error/bosch-ui-validation-error.component';
import { provideCustomCVA } from '../../basics/ui-custom-cva.provider';
import { TranslateModule } from '@ngx-translate/core';
import { BoschUiIconComponent } from '../bosch-ui-icon/bosch-ui-icon.component';
import { BoschUiIconNames } from '../bosch-ui-icon/bosch-ui-icon.constants';
import { BoschUiInputAutofocusDirective } from '../../directives/bosch-ui-input-autofocus-directive/bosch-ui-input-autofocus.directive';
import { BoschUiDisableControlDirective } from '../../directives/bosch-ui-disable-control/bosch-ui-disable-control.directive';
import { InputWithAutofocus } from '../../basics/models/bosch-ui-input-with-autofocus.interface';
import { AutofocusDirective } from '../../basics/models/bosch-ui-autofocus-directive.interface';

@Component({
  selector: 'bosch-ui-input-search',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BoschUiValidationErrorComponent,
    TranslateModule,
    BoschUiIconComponent,
    BoschUiInputAutofocusDirective,
    BoschUiDisableControlDirective,
  ],
  templateUrl: './bosch-ui-input-search.component.html',
  styleUrls: ['./bosch-ui-input-search.component.scss'],
  providers: [provideCustomCVA(BoschUiInputSearchComponent)],
  exportAs: 'input-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BoschUiInputSearchComponent
  extends UiControlValueAccessorDirective<string | null>
  implements OnInit, InputWithAutofocus
{
  @Input() label: string | null = null;
  @Input() placeholder = '';
  @Input() readonly = false;
  @Input() autofocus = false;
  @Output() searchClick = new EventEmitter<string | null>();
  @Output() clearClick = new EventEmitter<void>();
  @ViewChild(BoschUiInputAutofocusDirective) autofocusDirective!: AutofocusDirective;
  @ContentChild(BoschUiValidationErrorComponent, { static: false }) errorRef: BoschUiValidationErrorComponent | null =
    null;
  readonly id: string = getRandomId('input-search');
  readonly iconNames = BoschUiIconNames;

  override ngOnInit(): void {
    super.ngOnInit();

    this.control.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value: string | null) => {
      this.onChange(value);
      this._value$.next(value);
    });
  }

  onSearch(): void {
    this.searchClick.next(this.control.getRawValue());
  }

  onClear(): void {
    this.control.setValue(null);
    this.clearClick.next();

    this.focus();
  }

  focus(): void {
    this.autofocusDirective.focus();
  }
}
